import { faArrowsAltV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import arrayMove from 'array-move';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AdminRanks from './AdminRanks';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clone } from 'lodash';
import { toast } from 'react-toastify';

// Optimized Drag Handle Component with performance improvements
const DragHandle = ({
  sliderValue,
  backgroundColor,
  pk_Candidate,
  isUpdatingRankList,
  isFetchingCandidates,
  isScoreSortActive,
}) => {
  const FONT_SIZES = [15, 15, 30, 40];
  const isDisabled = isUpdatingRankList || isFetchingCandidates || isScoreSortActive;

  const handleStyle = useMemo(
    () => ({
      display: 'flex',
      width: 60,
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 10,
      backgroundColor: 'transparent',
      cursor: isDisabled ? 'not-allowed' : 'grab',
    }),
    [backgroundColor, isDisabled],
  );

  return (
    <div id={`dragHandle_${pk_Candidate}`} style={handleStyle}>
      <FontAwesomeIcon icon={faArrowsAltV} style={{ fontSize: FONT_SIZES[sliderValue] }} />
    </div>
  );
};

// Optimized Sortable Item using @dnd-kit
const SortableGridItem = ({ id, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const {
    i,
    candidates,
    setCandidateRanks,
    setSelectedUser,
    candidate,
    sliderValue,
    postChange,
    selectedUser,
    isFetchingCandidates,
    isCollapsed,
    unique_key,
    setLastJumpTo,
    detailNotesHaveChange,
    viewMode,
    headerWidths,
    isCard,
    isScoreSortActive,
    enableRevisedRankScores,
    onRevisedScoreChange,
  } = props;
  const { pk_Candidate } = candidate;

  const itemStyle = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging],
  );

  // For Grid mode, we need a different wrapper
  if (viewMode === 'Grid') {
    return (
      <div
        ref={setNodeRef}
        style={{
          ...itemStyle,
          backgroundColor: 'white',
          borderTopRightRadius: '15px',
          borderBottomRightRadius: '15px',
          borderTopLeftRadius: '0px',
          borderBottomLeftRadius: '0px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          cursor: 'grab',
        }}
        {...attributes}
        {...listeners}
      >
        <AdminRanks
          detailNotesHaveChange={detailNotesHaveChange}
          candidate={candidate}
          candidates={candidates}
          id={id}
          i={i}
          isFetchingCandidates={isFetchingCandidates}
          key={`key_${pk_Candidate}_${i}_${unique_key}`}
          postChange={postChange}
          selectedUser={selectedUser}
          setCandidateRanks={setCandidateRanks}
          setSelectedUser={setSelectedUser}
          sliderValue={sliderValue}
          unique_key={unique_key}
          setLastJumpTo={setLastJumpTo}
          viewMode={viewMode}
          headerWidths={headerWidths}
          enableRevisedRankScores={enableRevisedRankScores}
          onRevisedScoreChange={onRevisedScoreChange}
          isCard={false} // Grid mode uses its own rendering
          DragHandle={(dragProps) => (
            <div>
              <DragHandle {...dragProps} isScoreSortActive={isScoreSortActive} />
            </div>
          )}
        />
      </div>
    );
  }

  if (!isCard) {
    return (
      <AdminRanks
        detailNotesHaveChange={detailNotesHaveChange}
        candidate={candidate}
        candidates={candidates}
        id={id}
        i={i}
        isFetchingCandidates={isFetchingCandidates}
        key={`key_${pk_Candidate}_${i}_${unique_key}`}
        postChange={postChange}
        selectedUser={selectedUser}
        setCandidateRanks={setCandidateRanks}
        setSelectedUser={setSelectedUser}
        sliderValue={sliderValue}
        unique_key={unique_key}
        setLastJumpTo={setLastJumpTo}
        viewMode={viewMode}
        headerWidths={headerWidths}
        enableRevisedRankScores={enableRevisedRankScores}
        onRevisedScoreChange={onRevisedScoreChange}
        rowRef={setNodeRef}
        rowAttributes={attributes}
        rowStyleOverride={itemStyle}
        DragHandle={(dragProps) => (
          <div {...(isScoreSortActive ? {} : listeners)}>
            <DragHandle {...dragProps} isScoreSortActive={isScoreSortActive} />
          </div>
        )}
      />
    );
  }
  return (
    <div ref={setNodeRef} style={itemStyle} {...attributes}>
      <AdminRanks
        candidate={candidate}
        candidates={candidates}
        detailNotesHaveChange={detailNotesHaveChange}
        headerWidths={headerWidths}
        i={i}
        id={id}
        isFetchingCandidates={isFetchingCandidates}
        isCard={isCard}
        key={`key_${pk_Candidate}_${i}_${unique_key}`}
        postChange={postChange}
        selectedUser={selectedUser}
        setCandidateRanks={setCandidateRanks}
        setLastJumpTo={setLastJumpTo}
        setSelectedUser={setSelectedUser}
        sliderValue={sliderValue}
        unique_key={unique_key}
        viewMode={viewMode}
        enableRevisedRankScores={enableRevisedRankScores}
        onRevisedScoreChange={onRevisedScoreChange}
        DragHandle={(dragProps) => (
          <div {...(isScoreSortActive ? {} : listeners)}>
            <DragHandle {...dragProps} isScoreSortActive={isScoreSortActive} />
          </div>
        )}
      />
    </div>
  );
};

const AdminRanksSort = ({
  candidates = [],
  setCandidateRanks,
  setSelectedUser,
  sliderValue,
  postChange,
  selectedUser,
  isFetchingCandidates,
  isUpdatingRankList,
  isCollapsed,
  isCard,
  unique_key,
  setLastJumpTo,
  detailNotesHaveChange,
  viewMode,
  headerWidths,
  isScoreSortActive,
  enableRevisedRankScores,
  onCancelRevisedScoreSave,
  onRevisedScoreChange,
  ...props
}) => {
  const [items, setItems] = useState({ candidates: candidates });
  const activeDragIdRef = useRef(null);
  const shouldCancelDropRef = useRef(false);

  // Optimized sensors for @dnd-kit with performance improvements
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Memoized candidates array for performance
  const candidateIds = useMemo(() => candidates.map((candidate) => candidate.pk_Candidate), [candidates]);

  // Determine the sorting strategy based on viewMode
  const sortingStrategy = useMemo(() => {
    return viewMode === 'Grid' ? rectSortingStrategy : verticalListSortingStrategy;
  }, [viewMode]);

  // Determine if we should render as grid
  const isGridMode = useMemo(() => viewMode === 'Grid', [viewMode]);

  useEffect(() => {
    const forceEndActiveDrag = () => {
      if (typeof window === 'undefined') {
        return;
      }

      if (typeof PointerEvent !== 'undefined') {
        window.dispatchEvent(new PointerEvent('pointerup', { bubbles: true }));
      }

      if (typeof MouseEvent !== 'undefined') {
        window.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      }

      if (typeof Event !== 'undefined') {
        window.dispatchEvent(new Event('touchend', { bubbles: true }));
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || activeDragIdRef.current == null) {
        return;
      }

      shouldCancelDropRef.current = true;
      toast.info('Reorder cancelled');
      forceEndActiveDrag();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const handleDragStart = useCallback((event) => {
    shouldCancelDropRef.current = false;
    activeDragIdRef.current = event?.active?.id ?? null;
  }, []);

  const handleDragCancel = useCallback(() => {
    shouldCancelDropRef.current = false;
    activeDragIdRef.current = null;
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      const shouldCancelDrop = shouldCancelDropRef.current;

      shouldCancelDropRef.current = false;
      activeDragIdRef.current = null;

      if (shouldCancelDrop) {
        return;
      }

      if (isScoreSortActive) {
        return;
      }

      if (active.id !== over?.id) {
        const oldIndex = candidates.findIndex((candidate) => candidate.pk_Candidate === active.id);
        const newIndex = candidates.findIndex((candidate) => candidate.pk_Candidate === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newCandidates = arrayMove(candidates, oldIndex, newIndex);
          setCandidateRanks(newCandidates);

          // Call postChange with the moved candidate information
          const movedCandidate = candidates[oldIndex];
          postChange(
            newCandidates,
            candidates,
            `Moved ${movedCandidate.FirstName} ${movedCandidate.LastName} from rank ${oldIndex + 1} to ${newIndex + 1}`,
            movedCandidate.pk_Candidate,
            oldIndex,
            newIndex,
          );
        }
      }
    },
    [candidates, isScoreSortActive, setCandidateRanks, postChange],
  );

  // Update items when candidates change
  useEffect(() => {
    setItems({ candidates });
  }, [candidates]);

  // Memoized render function for performance
  const renderSortableItems = useMemo(() => {
    return candidates.map((candidate, index) => (
      <SortableGridItem
        candidate={candidate}
        candidates={candidates}
        detailNotesHaveChange={detailNotesHaveChange}
        enableRevisedRankScores={enableRevisedRankScores}
        headerWidths={headerWidths}
        i={index}
        id={candidate.pk_Candidate}
        isCollapsed={isCollapsed}
        isFetchingCandidates={isFetchingCandidates}
        key={candidate.pk_Candidate}
        isScoreSortActive={isScoreSortActive}
        onCancelRevisedScoreSave={onCancelRevisedScoreSave}
        onRevisedScoreChange={onRevisedScoreChange}
        postChange={postChange}
        selectedUser={selectedUser}
        setCandidateRanks={setCandidateRanks}
        setLastJumpTo={setLastJumpTo}
        setSelectedUser={setSelectedUser}
        sliderValue={sliderValue}
        unique_key={unique_key}
        viewMode={viewMode}
        isCard={isCard}
      />
    ));
  }, [
    candidates,
    setCandidateRanks,
    setSelectedUser,
    sliderValue,
    postChange,
    selectedUser,
    isFetchingCandidates,
    isCollapsed,
    unique_key,
    setLastJumpTo,
    detailNotesHaveChange,
    enableRevisedRankScores,
    viewMode,
    headerWidths,
    isCard,
    isScoreSortActive,
    onCancelRevisedScoreSave,
    onRevisedScoreChange,
  ]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={candidateIds} strategy={sortingStrategy}>
        {isGridMode ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(200px, 1fr))`,
              gap: '16px',
              padding: '16px',
              width: '100%',
              backgroundColor: '#f5f5f5', // Light gray background for grid
            }}
          >
            {renderSortableItems}
          </div>
        ) : isCard ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>{renderSortableItems}</div>
        ) : (
          <tbody
          // style={{ visibility: isCollapsed ? 'collapse' : 'visible' }}
          >
            {renderSortableItems}
          </tbody>
        )}
      </SortableContext>
    </DndContext>
  );
};

export default AdminRanksSort;
